import type { FinancialYear } from "../types.js";

// take-home ruleset for FY2025-26 (1 July 2025 – 30 June 2026).
//
// take-home owns NO tax tables — every bracket, taper, levy and HELP band lives
// in the shared `income-tax` FY ruleset (../income-tax/fy2025-26.ts), CITED and
// unit-tested there. This file holds only the handful of figures the net-pay
// MODES need that are NOT part of an income-tax assessment, plus the pointer to
// the income-tax FY to resolve and the mode-specific provenance.
//
// Never edit a shipped ruleset to "fix" a later year — author a new
// fyXXXX-XX.ts and register it.
export interface TakeHomeRuleSet {
  /** Mode-engine tag — distinguishes this from a per-product (au0x) ruleset. */
  engine: "take-home";
  /** Financial-year key, e.g. "2025-26". */
  fy: FinancialYear;
  /** The income-tax FY ruleset key this product resolves against. */
  incomeTaxFy: FinancialYear;
  /** Inclusive FY start (1 July). */
  effectiveFrom: string;
  /** Inclusive FY end (30 June). */
  effectiveTo: string;
  /** Source-of-truth URLs the mode-specific figures were transcribed from. */
  sources: Record<string, string>;
  /** Date the source pages were last verified, ISO. */
  sourcesVerifiedAt: string;
  /**
   * Tax-free threshold for a full-year resident ($18,200). Used by the
   * second-job mode as the slice the second payer wrongly treats as tax-free.
   * Mirrors income-tax `taxFreeThreshold` for the same FY (second-job ledger
   * #3, CITED — ATO "$18,200 / $700 a fortnight").
   */
  taxFreeThreshold: number;
}

export const fy2025_26: TakeHomeRuleSet = {
  engine: "take-home",
  fy: "2025-26",
  incomeTaxFy: "2025-26",
  effectiveFrom: "2025-07-01",
  effectiveTo: "2026-06-30",
  sources: {
    // second-job + withholding-gap: tax-free threshold as the withholding break
    // point (withholding-gap ledger #1; second-job ledger #3, both CITED).
    taxFreeThreshold:
      "https://www.ato.gov.au/individuals-and-families/jobs-and-employment-types/tax-free-threshold/how-to-claim-the-tax-free-threshold",
    // help-not-withheld: STSL withholding is opt-in; silence ⇒ lump-sum bill
    // (help-not-withheld ledger #1, CITED).
    stslOptIn:
      "https://www.ato.gov.au/individuals-and-families/study-and-training-support-loans/tell-your-employer-about-your-study-or-training-loan",
    // compulsory repayment assessed in full at lodgement regardless of
    // withholding (help-not-withheld ledger #2, CITED).
    compulsoryRepayment:
      "https://www.ato.gov.au/individuals-and-families/study-and-training-support-loans/compulsory-repayments",
    // multiple jobs / threshold-on-two-payers trap (second-job ledger #2/#4).
    multipleJobs:
      "https://www.ato.gov.au/individuals-and-families/jobs-and-employment-types/tax-free-threshold/multiple-jobs-or-change-of-job",
  },
  sourcesVerifiedAt: "2026-06-22",
  taxFreeThreshold: 18_200,
};
